/**
 * Geometric overlay SVG paths for procedure types.
 * Each function returns an array of SVG path `d` strings for a given tooth shape.
 * viewBox is "0 0 50 100" (same as ToothSVG).
 */

import { estimatePathBounds } from "./toothChartUtils";
import { getToothPaths } from "./toothPathData";

/** Crown bounds per shape — re-exported from ToothSVG constants */
const CROWN_BOUNDS = {
  molar: {
    left: 7,
    right: 39,
    upperCy: 52,
    lowerCy: 48,
    halfH: 14
  },
  premolar: {
    left: 11,
    right: 34,
    upperCy: 52,
    lowerCy: 48,
    halfH: 14
  },
  canine: {
    left: 13,
    right: 32,
    upperCy: 52,
    lowerCy: 48,
    halfH: 14
  },
  incisor: {
    left: 15,
    right: 30,
    upperCy: 52,
    lowerCy: 48,
    halfH: 14
  }
};

/** Root regions per shape */
const ROOT_BOUNDS = {
  molar: {
    upperTop: 10,
    upperBot: 40,
    lowerTop: 60,
    lowerBot: 90
  },
  premolar: {
    upperTop: 12,
    upperBot: 40,
    lowerTop: 60,
    lowerBot: 88
  },
  canine: {
    upperTop: 6,
    upperBot: 40,
    lowerTop: 60,
    lowerBot: 94
  },
  incisor: {
    upperTop: 10,
    upperBot: 40,
    lowerTop: 60,
    lowerBot: 90
  }
};
function getCrownRect(shape, upper, inset = 2) {
  const b = CROWN_BOUNDS[shape];
  const cy = upper ? b.upperCy : b.lowerCy;
  const l = b.left + inset;
  const r = b.right - inset;
  const t = cy - b.halfH + inset;
  const bot = cy + b.halfH - inset;
  return {
    l,
    r,
    t,
    bot,
    cx: (l + r) / 2,
    cy,
    w: r - l,
    h: bot - t
  };
}

/** Crown overlay — when toothNumber provided, uses actual crown path as boundary */
export function getCrownOverlayPaths(shape, upper, toothNumber) {
  if (toothNumber) {
    const paths = getToothPaths(toothNumber);
    if (paths?.crown) {
      const bounds = estimatePathBounds(paths.crown);
      // Use the actual crown path as the outline, plus detail lines across crown bounds
      const h1y = bounds.minY + (bounds.maxY - bounds.minY) * 0.33;
      const h2y = bounds.minY + (bounds.maxY - bounds.minY) * 0.66;
      const line1 = `M${bounds.minX + 4},${h1y} L${bounds.maxX - 4},${h1y}`;
      const line2 = `M${bounds.minX + 6},${h2y} L${bounds.maxX - 6},${h2y}`;
      return [paths.crown, line1, line2];
    }
  }

  // Geometric fallback
  const {
    l,
    r,
    t,
    bot,
    cx
  } = getCrownRect(shape, upper, 2);
  const rx = 3;
  const outline = `M${l + rx},${t} L${r - rx},${t} Q${r},${t} ${r},${t + rx} L${r},${bot - rx} Q${r},${bot} ${r - rx},${bot} L${l + rx},${bot} Q${l},${bot} ${l},${bot - rx} L${l},${t + rx} Q${l},${t} ${l + rx},${t} Z`;
  const h1 = t + (bot - t) * 0.33;
  const h2 = t + (bot - t) * 0.66;
  const line1 = `M${l + 2},${h1} L${r - 2},${h1}`;
  const line2 = `M${l + 4},${h2} L${r - 4},${h2}`;
  return [outline, line1, line2];
}

/** 3/4 crown — partial coverage */
export function getCrown34OverlayPaths(shape, upper) {
  const {
    l,
    r,
    t,
    bot
  } = getCrownRect(shape, upper, 3);
  const rx = 3;
  const cutoff = l + (r - l) * 0.25;
  const outline = `M${cutoff},${t} L${r - rx},${t} Q${r},${t} ${r},${t + rx} L${r},${bot - rx} Q${r},${bot} ${r - rx},${bot} L${cutoff},${bot}`;
  return [outline];
}

/** Core buildup — positioned at actual crown-root junction when toothNumber provided */
export function getCoreOverlayPaths(shape, upper, toothNumber) {
  if (toothNumber) {
    const paths = getToothPaths(toothNumber);
    if (paths?.crown) {
      const crownBounds = estimatePathBounds(paths.crown);
      const cx = crownBounds.cx;
      // Position at the root-side of the crown
      const junctionY = upper ? crownBounds.minY + 2 : crownBounds.maxY - 2;
      const cw = (crownBounds.maxX - crownBounds.minX) * 0.5;
      const ch = (crownBounds.maxY - crownBounds.minY) * 0.3;
      const t = upper ? junctionY : junctionY - ch;
      const bot = upper ? junctionY + ch : junctionY;
      return [`M${cx - cw / 2},${t} L${cx + cw / 2},${t} L${cx + cw / 2},${bot} L${cx - cw / 2},${bot} Z`];
    }
  }
  const {
    cx,
    cy,
    w,
    h
  } = getCrownRect(shape, upper, 2);
  const cw = w * 0.6;
  const ch = h * 0.5;
  return [`M${cx - cw / 2},${cy - ch / 2} L${cx + cw / 2},${cy - ch / 2} L${cx + cw / 2},${cy + ch / 2} L${cx - cw / 2},${cy + ch / 2} Z`];
}

/** Root canal fill — traces thin lines along root path centerlines */
export function getRootCanalPaths(shape, upper, toothNumber) {
  if (toothNumber) {
    const paths = getToothPaths(toothNumber);
    if (paths?.root) {
      const rootBounds = estimatePathBounds(paths.root);
      const cx = rootBounds.cx;
      const top = rootBounds.minY + 4;
      const bot = rootBounds.maxY - 2;
      if (shape === "molar") {
        const third = (rootBounds.maxX - rootBounds.minX) / 3;
        return [`M${rootBounds.minX + third * 0.5},${top + 6} L${rootBounds.minX + third * 0.5},${bot - 4}`, `M${cx},${top + 2} L${cx},${bot}`, `M${rootBounds.maxX - third * 0.5},${top + 6} L${rootBounds.maxX - third * 0.5},${bot - 4}`];
      }
      if (shape === "premolar") {
        const quarter = (rootBounds.maxX - rootBounds.minX) / 4;
        return [`M${rootBounds.minX + quarter},${top + 4} L${rootBounds.minX + quarter},${bot - 4}`, `M${rootBounds.maxX - quarter},${top + 4} L${rootBounds.maxX - quarter},${bot - 4}`];
      }
      return [`M${cx},${top + 2} L${cx},${bot}`];
    }
  }

  // Geometric fallback
  const rb = ROOT_BOUNDS[shape];
  const top = upper ? rb.upperTop : rb.lowerTop;
  const bot = upper ? rb.upperBot : rb.lowerBot;
  if (shape === "molar") {
    return [`M14,${top + 8} L14,${bot - 4}`, `M22.5,${top + 4} L22.5,${bot - 2}`, `M34,${top + 8} L34,${bot - 4}`];
  }
  if (shape === "premolar") {
    return [`M18,${top + 6} L18,${bot - 4}`, `M27,${top + 6} L27,${bot - 4}`];
  }
  return [`M22.5,${top + 4} L22.5,${bot - 2}`];
}

/** Implant — trapezoidal screw with thread lines */
export function getImplantPaths(shape, upper) {
  const rb = ROOT_BOUNDS[shape];
  const top = upper ? rb.upperTop + 2 : rb.lowerTop + 2;
  const bot = upper ? rb.upperBot - 1 : rb.lowerBot - 1;
  const topW = 6;
  const botW = 2.5;
  const cx = 22.5;
  const body = `M${cx - topW},${top} L${cx + topW},${top} L${cx + botW},${bot} L${cx - botW},${bot} Z`;
  const threads = [];
  const count = 5;
  for (let i = 1; i <= count; i++) {
    const y = top + (bot - top) / (count + 1) * i;
    const frac = i / (count + 1);
    const hw = topW - (topW - botW) * frac;
    threads.push(`M${cx - hw - 1},${y} L${cx + hw + 1},${y}`);
  }
  return [body, ...threads];
}

/** Post — vertical rod from mid-root to mid-crown */
export function getPostPaths(shape, upper) {
  const rb = ROOT_BOUNDS[shape];
  const b = CROWN_BOUNDS[shape];
  const rootMid = upper ? (rb.upperTop + rb.upperBot) / 2 : (rb.lowerTop + rb.lowerBot) / 2;
  const crownMid = upper ? b.upperCy : b.lowerCy;
  return [`M22.5,${rootMid} L22.5,${crownMid}`];
}

/** Sealant — thin path following groove/occlusal surface */
export function getSealantPaths(shape, upper) {
  const b = CROWN_BOUNDS[shape];
  const cy = upper ? b.upperCy : b.lowerCy;
  const l = b.left + 4;
  const r = b.right - 4;
  const cx = (b.left + b.right) / 2;
  if (shape === "molar" || shape === "premolar") {
    const y = upper ? cy + b.halfH - 6 : cy - b.halfH + 6;
    return [`M${l},${y} Q${cx},${y + (upper ? 3 : -3)} ${r},${y}`];
  }
  const y = upper ? cy + b.halfH - 4 : cy - b.halfH + 4;
  return [`M${l + 2},${y} L${r - 2},${y}`];
}

/** Abutment — truncated cone above implant */
export function getAbutmentPaths(shape, upper) {
  const b = CROWN_BOUNDS[shape];
  const rb = ROOT_BOUNDS[shape];
  const crownBot = upper ? b.upperCy + b.halfH : b.lowerCy - b.halfH;
  const rootTop = upper ? rb.upperBot : rb.lowerTop;
  const cx = 22.5;
  const topW = 4;
  const botW = 2.5;
  const top = upper ? crownBot : rootTop;
  const bot = upper ? rootTop : crownBot;
  return [`M${cx - topW},${top} L${cx + topW},${top} L${cx + botW},${bot} L${cx - botW},${bot} Z`];
}

/** Veneer — thin layer on facial surface */
export function getVeneerPaths(shape, upper) {
  const b = CROWN_BOUNDS[shape];
  const cy = upper ? b.upperCy : b.lowerCy;
  const l = b.left + 2;
  const r = b.right - 2;
  const t = cy - b.halfH + 2;
  const thickness = 3;
  const y = upper ? t : cy + b.halfH - 2 - thickness;
  return [`M${l},${y} L${r},${y} L${r},${y + thickness} L${l},${y + thickness} Z`];
}
/** Map procedure type to its path generator */
export const PROCEDURE_PATH_GETTERS = {
  crown: getCrownOverlayPaths,
  crown34: getCrown34OverlayPaths,
  core: getCoreOverlayPaths,
  roots: getRootCanalPaths,
  implant: getImplantPaths,
  post: getPostPaths,
  sealant: getSealantPaths,
  abutment: getAbutmentPaths,
  veneer: getVeneerPaths
};

/** Whether this procedure type should render as stroke-only (vs filled).
 *  Stroke-only procedures show as bold outlines so tooth enamel/grooves remain visible. */
export const STROKE_ONLY_PROCEDURES = new Set(["crown", "roots", "post", "sealant", "crown34"]);

/** Procedures that are anatomically root-dominant and should render on buccal only in dual-view mode. */
export const ROOT_DOMINANT_PROCEDURES = new Set(["implant", "roots", "post", "abutment"]);

/**
 * View-aware procedure path lookup for the Curve dual-view chart.
 * Keeps legacy behavior unchanged when callers still use PROCEDURE_PATH_GETTERS directly.
 */
export function getProcedurePathsForView(type, shape, upper, toothNumber, view) {
  if (view === "lingual" && ROOT_DOMINANT_PROCEDURES.has(type)) {
    return [];
  }
  const getter = PROCEDURE_PATH_GETTERS[type];
  if (!getter) return [];
  return getter(shape, upper, toothNumber);
}